import type { Company } from '../@types/Company';
import { SOFTWARE_CONFIGS } from '../@types/Company';
import { extractFilenameFromContentDisposition } from './fileDownloadUtils';
import { JWTAuthorization } from './JWTHeader';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function getSoftwareDisplayName(softwareId: string | null | undefined): string {
  if (!softwareId) {return '-';}
  const config = SOFTWARE_CONFIGS.find((c) => c.id === softwareId);
  return config?.displayName ?? softwareId;
}

// The config export is considered imported once the company validated its copros
export function isCoproValidated(company: Pick<Company, 'setupSteps'>): boolean {
  return company.setupSteps?.coproValidation === true;
}

/**
 * Human readable age of an upload, counted in calendar days ("aujourd'hui", "hier", "il y a 3 jours").
 */
export function formatUploadAge(dateString: string, now: Date = new Date()): string {
  const uploadDay = new Date(dateString);
  uploadDay.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const days = Math.max(0, Math.round((today.getTime() - uploadDay.getTime()) / DAY_IN_MS));
  if (days === 0) {return 'aujourd\'hui';}
  if (days === 1) {return 'hier';}
  return `il y a ${days} jours`;
}

export async function downloadCompanyConfigExport(companyId: number, companyName: string, jwt: string): Promise<void> {
  const response = await fetch(`/api/admin/pending-config-imports/${companyId}/download`, {
    headers: {
      Authorization: JWTAuthorization(jwt),
    },
  });
  if (!response.ok) {
    throw new Error('Download failed');
  }
  const blob = await response.blob();
  const filename = extractFilenameFromContentDisposition(
    response,
    `export-${companyName.replaceAll(' ', '-')}.xlsx`,
  );
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}
